import type { ReactNode } from 'react';
import { useState } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { BackButton } from '@sorare/core/src/components/dialog/BackButton';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import type {
  AlgoliaCardIndexesName,
  GallerySortName,
  PrintablePlayerSortName,
} from '@sorare/core/src/contexts/config';
import {
  useClearAllFilters as useBackendClearAllFilters,
  useFiltersCount as useBackendFiltersCount,
  useGQLSearchContext,
} from '@sorare/core/src/contexts/graphql-search';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import type { FilterWidget, FiltersGroup } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { useClearAllFilters } from '#src/search/ClearAllFilters/index.tsx';
import { Filters } from '#src/search/FiltersManager/index.tsx';
import { useFiltersCount } from '#src/search/FiltersManager/useFiltersCount.ts';

import { useSortWidget } from './useSortWidget.tsx';

type Props = {
  sorts?: (
    | AlgoliaCardIndexesName
    | GallerySortName
    | PrintablePlayerSortName
  )[];
  open: boolean;
  onClose: () => void;
  filtersGroups: FiltersGroup[];
  children?: ReactNode;
};

const messages = defineMessages({
  title: {
    defaultMessage: 'Filters',
  },
});

const Body = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
  flex-grow: 1;
  background-color: var(--c-black);
  padding: var(--triple-unit);
  @media ${tabletAndAbove} {
    width: 350px;
  }
`;

const Header = styled(Horizontal)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ButtonsWrapper = styled(Vertical).attrs({ center: true })`
  width: 100%;
  position: sticky;
  bottom: 0;
  padding: var(--triple-unit);
  border-top: 1px solid var(--c-nd-150);
  background-color: var(--c-black);
  justify-content: flex-start;
`;

const StyledDrawer = styled(Drawer)`
  width: 100%;
  max-width: 100%;
  height: 100%;
  ${regularMaterial}
  @media ${tabletAndAbove} {
    width: 370px;
    border-left: 1px solid var(--c-nd-150);
  }
`;

export const FiltersManagerDialog = ({
  sorts,
  open,
  onClose: onCloseDialog,
  filtersGroups,
  children,
}: Props) => {
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const [activeWidget, setActiveWidget] = useState<FilterWidget>();
  const clearAllFilters = useClearAllFilters();
  const filtersCount = useFiltersCount();
  const { results } = useInstantSearch();

  const sortWidget = useSortWidget({ sorts: sorts ?? [] });
  const groups = [
    ...(sorts
      ? [
          {
            slug: 'sort',
            filters: [sortWidget],
          },
        ]
      : []),
    ...(filtersGroups || []),
  ];

  const onClose = () => {
    setActiveWidget(undefined);
    onCloseDialog();
  };

  return (
    <StyledDrawer
      open={open}
      side={isTabletOrAbove ? 'right' : 'bottom'}
      onBackdropClick={() =>
        activeWidget ? setActiveWidget(undefined) : onClose()
      }
      keepMounted
    >
      <Body>
        <Header>
          <BackButton
            onBack={() =>
              activeWidget ? setActiveWidget(undefined) : onClose()
            }
          />
          {activeWidget ? (
            activeWidget.title
          ) : (
            <BodyM bold>
              <FormattedMessage {...messages.title} />
            </BodyM>
          )}
          <CloseButton onClose={onClose} />
        </Header>

        {children}
        <Vertical gap={0}>
          <Filters filters={groups} />
        </Vertical>
      </Body>
      <ButtonsWrapper>
        <Button onClick={onClose} size="medium" color="primary" fullWidth>
          <FormattedMessage
            defaultMessage="Show {results, number} results"
            values={{
              results: results ? results.nbHits : 0,
            }}
          />
        </Button>
        <Button
          color="red"
          size="medium"
          onClick={clearAllFilters}
          disabled={!filtersCount}
          fullWidth
        >
          <BodyM>
            <FormattedMessage {...filters.clearAll} />
          </BodyM>
        </Button>
      </ButtonsWrapper>
    </StyledDrawer>
  );
};

export const BackendFiltersManagerDialog = ({
  open,
  onClose: onCloseDialog,
  filtersGroups,
  children,
}: Props) => {
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const [activeWidget, setActiveWidget] = useState<FilterWidget>();

  const clearAllFilters = useBackendClearAllFilters();
  const { nbHits } = useGQLSearchContext()!;
  const filtersCount = useBackendFiltersCount();
  const groups = [...(filtersGroups || [])];

  const onClose = () => {
    setActiveWidget(undefined);
    onCloseDialog();
  };

  return (
    <StyledDrawer
      open={open}
      side={isTabletOrAbove ? 'right' : 'bottom'}
      onBackdropClick={() =>
        activeWidget ? setActiveWidget(undefined) : onClose()
      }
      keepMounted
    >
      <Body>
        <Header>
          <BackButton
            onBack={() =>
              activeWidget ? setActiveWidget(undefined) : onClose()
            }
          />
          {activeWidget ? (
            activeWidget.title
          ) : (
            <BodyM bold>
              <FormattedMessage {...messages.title} />
            </BodyM>
          )}
          <CloseButton onClose={onClose} />
        </Header>

        {children}
        <Vertical gap={0}>
          <Filters filters={groups} />
        </Vertical>
      </Body>
      <ButtonsWrapper>
        <Button onClick={onClose} size="medium" color="primary" fullWidth>
          <FormattedMessage
            defaultMessage="Show {results, number} results"
            values={{
              results: nbHits,
            }}
          />
        </Button>
        <Button
          color="red"
          size="medium"
          onClick={clearAllFilters}
          disabled={!filtersCount}
          fullWidth
        >
          <BodyM>
            <FormattedMessage {...filters.clearAll} />
          </BodyM>
        </Button>
      </ButtonsWrapper>
    </StyledDrawer>
  );
};
